import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getAccessToken, getUser, clearAccessToken, clearUser } from 'api/tokenService'
import { login as apiLogin, logout as apiLogout, fetchMePermissions, fetchPermissions, UserWithRolesAndPermissions } from 'api/auth'

type Permission = any

interface AuthContextValue {
  isAuthenticated: boolean
  authLoading: boolean
  permissions: Permission[]
  permissionsLoading: boolean
  user: unknown | null
  userRoles: string[]
  userInfo: UserWithRolesAndPermissions | null
  login: (ecode: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshPermissions: () => Promise<void>
  hasRole: (role: string) => boolean

  menuPermissions: any[]
  menuPermissionsLoading: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authLoading, setAuthLoading] = useState(true)
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [user, setUser] = useState<unknown | null>(getUser())
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [userInfo, setUserInfo] = useState<UserWithRolesAndPermissions | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAccessToken())

  const [menuPermissions, setMenuPermissions] = useState<any[]>([])
  const [menuPermissionsLoading, setMenuPermissionsLoading] = useState(false)


const refreshPermissions = useCallback(async () => {
  setPermissionsLoading(true)
  try {
    const userData = await fetchMePermissions()
    setPermissions(userData.perms as unknown as Permission[])
    setUserRoles(userData.roles)
    setUserInfo(userData)
    setUser(userData)

    // NEW: fetch menu permissions
    setMenuPermissionsLoading(true)
    try {
      const menus = await fetchPermissions()
      setMenuPermissions(menus)
    } finally {
      setMenuPermissionsLoading(false)
    }

  } finally {
    setPermissionsLoading(false)
  }
}, [])

  const hasRole = useCallback((role: string): boolean => {
    return userRoles.includes(role)
  }, [userRoles])

  const login = useCallback(async (ecode: string, password: string) => {
    await apiLogin(ecode, password)
    setUser(getUser())
    setIsAuthenticated(true)
    await refreshPermissions()
  }, [refreshPermissions])

  const logout = useCallback(async () => {
    try { await apiLogout() } finally {
      clearAccessToken(); clearUser();
      setUser(null)
      setPermissions([])
      setUserRoles([])
      setUserInfo(null)
      setIsAuthenticated(false)
    }
  }, [])

  useEffect(() => {
    (async () => {
      try {
        if (isAuthenticated) {
          await refreshPermissions()
        }
      } catch (error) {
        // If refreshPermissions fails (e.g., session expired), clear auth state
        console.error('Failed to refresh permissions:', error)
        clearAccessToken()
        clearUser()
        setUser(null)
        setPermissions([])
        setUserRoles([])
        setUserInfo(null)
        setIsAuthenticated(false)
      } finally {
        setAuthLoading(false)
      }
    })()
  }, [])

  // Listen for session expiry events from httpClient
  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('Session expired event received, clearing auth state')
      clearAccessToken()
      clearUser()
      setUser(null)
      setPermissions([])
      setUserRoles([])
      setUserInfo(null)
      setIsAuthenticated(false)
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated,
    authLoading,
    permissions,
    permissionsLoading,
    user,
    userRoles,
    userInfo,
    login,
    logout,
    refreshPermissions,
    hasRole,
    menuPermissions,
    menuPermissionsLoading,
  }), [authLoading, isAuthenticated, login, logout, permissions, permissionsLoading, refreshPermissions, user, userRoles, userInfo, hasRole, menuPermissions, menuPermissionsLoading])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}


